/* eslint-disable react/require-default-props */
import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography, useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { addMinutes } from 'date-fns';
import {
  AppointmentType,
  AppointmentType as DBAppointmentType,
  VirtualMeetingProvider,
} from '@prisma/client';
import { Appointment, GeneralRequest, LoadedUser } from '../../interfaces';
import './form.css';
import './composer.css';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
  DEFAULT_CONSULT_LENGTH,
  ImageItem,
  getExistingImgList,
  getValidationSchema,
  initFormValues,
  isQueueWalkIn,
  isUnassignedAppointmentAllowed,
} from '../../utils/form/appointmentForm';
import {
  createAppointment,
  getAppointmentById,
  invalidateAppointment,
  invalidateAppointments,
  invalidateAvailability,
  invalidateDepositTransferAppointments,
  markDepositPaid,
  scheduleRequest,
  updateAppointment,
} from '../../services/appointments';
import AppointmentTypeComponent from './AppointmentType';
import ClientSearchAutofill from './ClientSearchAutofill';
import AppointmentDetails from './AppointmentDetails';
import TattooDetails from './TattooDetails';
import { addCustomerAlgolia, getRoleByPhoneNumber } from '../../services/user';
import InternalNotes from './InternalNotes';
import { AppContext } from '../../context/AppContext/context';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import useConfirm from '../../hooks/useConfirm';
import { isUserEmployeeOrAdmin } from '../../utils/global/helper';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import {
  getAppointmentFormErrorMessage,
  getCrossDatelineError,
  getWalkInStartDateTime,
  isAppointmentDepositPaid,
  isWaiverSelectable,
  validatePrice,
} from '../../helpers/appointments';
import { DEFAULT_TIMEZONE } from '../../constants/appointments';
import useWaiverRequestsTableActions from '../../hooks/waivers/useWaiverRequestsTableActions';
import {
  cancelWaiverRequest,
  reactivateWaiverRequest,
  requestWaiver,
} from '../../services/waivers';
import { isAppointmentWaiverUpdatable } from '../../helpers/waivers';
import useUser from '../../hooks/global/useUser';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import AppointmentDate from './AppointmentDate';
import useInitialAppointmentForm from '../../hooks/appointments/useInitialAppointmentForm';
import { searchClient } from '../../algoliaconfig';
import { invalidateQueue } from '../../services/queue';
import { SelectedAppointment } from '../../types/appointments';
import useCashDiscount from '../../hooks/payments/useCashDiscount';
import { cx } from '../../helpers/global';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import AppointmentSummary from './AppointmentSummary';
import { ModalHeaderBadge } from '../modal/FullPageModal';

export type CreateUpdateArgs =
  | {
      type: 'update';
      appointment: SelectedAppointment | GeneralRequest;
      newAptId?: string;
    }
  | {
      type: 'create';
      newAptId?: string;
    };

function getTypeLabel(values: CreateAptValues) {
  if (values.isBookingLink) return 'Booking link';
  if (values.isTouchUp) return 'Touch-up';
  if (values.appointmentType === 'CONSULTATION') return 'Consultation';
  if (values.appointmentType === 'WALK_IN') return 'Walk-in';
  return 'Appointment';
}

// The submit button is pinned to the modal header by the full-page modal css.
const headerSubmitSx = {
  height: 44,
  px: '26px',
  borderRadius: '50px',
  typography: 'h4',
  fontWeight: 700,
};

interface Props {
  selectedAppointment?: SelectedAppointment;
  selectedRequest?: Appointment | GeneralRequest;
  onClose: () => void;
  onCreateUpdateSuccess?: (args: CreateUpdateArgs) => void | Promise<void>;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  appointmentType?: DBAppointmentType;
  isBookingLink?: boolean;
  isTouchUp?: boolean;
}

export default function AppointmentForm({
  selectedAppointment: selectedAppointmentProp,
  selectedRequest,
  onClose,
  onCreateUpdateSuccess,
  setShouldScrollToTop,
  appointmentType: initialAppointmentType,
  isBookingLink: initialIsBookingLink,
  isTouchUp: initialIsTouchUp,
}: Props) {
  const confirm = useConfirm();
  const { user: loadedUser } = useUser();
  const {
    selectedAppointment: calendarSelectedAppointment,
    setSelectedAppointmentUpdated,
  } = useCalendarSelectedAppointment();
  const { updateLoadingModal, closeLoadingModal } = useLoadingModal();
  const { initialAppointmentForm, set } = useInitialAppointmentForm();
  const { cashDiscountEnabled } = useCashDiscount();
  const { active: redesignActive } = useBusinessWebRedesign();
  // Matches composer.css: one column and a docked summary bar below 1024px.
  const isSummaryDocked = useMediaQuery('(max-width:1023.98px)');

  const selectedAppointment =
    selectedAppointmentProp || calendarSelectedAppointment;

  const { data: initialWaiverData } = useGetWaivers({
    businessId: selectedAppointment?.businessId ?? selectedRequest?.businessId,
  });

  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const canAddAppointment = useFeatureGuard(Feature.ADD_APPOINTMENT);

  const [formError, setFormError] = React.useState<any>('');

  const [images, setImages] = React.useState<ImageItem[]>([]);

  const { state } = React.useContext(AppContext);
  const { piercingLocations } = state;

  const { resetWaiversPage } = useWaiverRequestsTableActions();

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  // do not want to run useEffect on initialWaiverData change
  const [waiverData] = React.useState(initialWaiverData);

  const firstAppointmentType = (initialAppointmentType ||
    initFormValues.appointmentType) as AppointmentType;
  const isInitialConsultation = firstAppointmentType === 'CONSULTATION';
  const isInitialWalkIn = firstAppointmentType === 'WALK_IN';
  const isInitialConsultationOrWalkIn =
    isInitialConsultation || isInitialWalkIn;
  const noInitialPrice = isInitialConsultationOrWalkIn || initialIsTouchUp;
  const [
    allowSwitchToBookingLinkFromScheduleNext,
    setAllowSwitchToBookingLinkFromScheduleNext,
  ] = React.useState(false);
  const allowSwitchToBookingLink =
    (selectedRequest && initialAppointmentType === 'CONSULTATION') ||
    allowSwitchToBookingLinkFromScheduleNext;

  const [loadedInitValues, setLoadedInitValues] =
    React.useState<CreateAptValues>({
      ...initFormValues,
      appointmentType: firstAppointmentType,
      depositPrice: noInitialPrice
        ? 0
        : loadedUser?.business.defaultTattooDeposit || 0,
      price: noInitialPrice
        ? 0
        : loadedUser?.business.defaultTattooDeposit || 0,
      requestWaiver:
        !initialIsTouchUp &&
        isWaiverSelectable({
          waivers: waiverData,
          hasWaiversFeature,
          appointmentType: firstAppointmentType,
          serviceId: null,
        }),
      appointmentLengthInMinutes: isInitialConsultation
        ? DEFAULT_CONSULT_LENGTH
        : DEFAULT_APT_LENGTH,
      depositPaid: false,
      isBookingLink: !!initialIsBookingLink,
      isTouchUp: !!initialIsTouchUp,
      isFromExistingAppointment: false,
      // only if true
      cashDiscountEnabled: cashDiscountEnabled || undefined,
    });

  React.useEffect(() => {
    if (!initialAppointmentForm) return;
    // Slot-click prefills are fresh appointments, not schedule-next copies.
    const isFromExistingAppointment =
      initialAppointmentForm.isFromExistingAppointment ?? true;
    setAllowSwitchToBookingLinkFromScheduleNext(isFromExistingAppointment);
    setLoadedInitValues(prev => ({
      ...prev,
      ...initialAppointmentForm,
      newImages: [],
      isFromExistingAppointment,
    }));
    if (initialAppointmentForm.newImages) {
      setImages(initialAppointmentForm.newImages);
    }
    set(null);
  }, [initialAppointmentForm, set]);

  React.useEffect(() => {
    if (selectedAppointment || selectedRequest) {
      const tempNewInitValues: CreateAptValues = { ...initFormValues };

      if (cashDiscountEnabled) {
        tempNewInitValues.cashDiscountEnabled = true;
      }

      const existingAppointment = (selectedAppointment ||
        selectedRequest) as SelectedAppointment;
      const appointmentType = selectedRequest
        ? initialAppointmentType || 'NEW_APPOINTMENT'
        : existingAppointment.appointmentType;
      const isWalkIn = appointmentType === 'WALK_IN';
      const isConsultation = appointmentType === 'CONSULTATION';
      const isWalkInOrConsultation = isWalkIn || isConsultation;
      const defaultLength = isConsultation
        ? DEFAULT_CONSULT_LENGTH
        : DEFAULT_APT_LENGTH;

      const hasFlashDesign =
        'flashDesign' in existingAppointment && existingAppointment.flashDesign;

      const showWaiverSelect = isWaiverSelectable({
        waivers: waiverData,
        hasWaiversFeature,
        appointmentType,
        serviceId: existingAppointment.serviceId,
      });

      tempNewInitValues.businessTripId =
        existingAppointment.businessTripId || '';
      tempNewInitValues.businessId = existingAppointment.businessId || '';
      tempNewInitValues.appointmentType = appointmentType;
      tempNewInitValues.appointmentLengthInMinutes = String(
        selectedRequest
          ? existingAppointment.appointmentLengthInMinutes || defaultLength
          : existingAppointment.appointmentLengthInMinutes || '',
      );
      const employeeStillActive =
        !existingAppointment.employeeId ||
        !existingAppointment.businessId ||
        existingAppointment.businessId !== loadedUser?.business?.id ||
        !!loadedUser?.business?.employees?.some(
          e => e.employeeId === existingAppointment.employeeId,
        );
      tempNewInitValues.employeeId = employeeStillActive
        ? existingAppointment.employeeId || ''
        : '';
      tempNewInitValues.customerId = existingAppointment.customerId;
      tempNewInitValues.depositPrice = existingAppointment.depositPrice || 0;

      if (existingAppointment.depositPaymentMethod) {
        tempNewInitValues.depositPaymentMethod =
          existingAppointment.depositPaymentMethod;
      }

      if (existingAppointment.endDateTime) {
        tempNewInitValues.endDateTime = new Date(
          existingAppointment.endDateTime,
        ).toISOString();
      }
      if (existingAppointment.startDateTime) {
        tempNewInitValues.startDateTime = new Date(
          existingAppointment.startDateTime,
        ).toISOString();
      }
      if (existingAppointment.priceType) {
        tempNewInitValues.priceType = existingAppointment.priceType;
      }
      tempNewInitValues.tattooColor = existingAppointment.tattooColor || '';
      tempNewInitValues.tattooDetails = existingAppointment.tattooDetails || '';
      tempNewInitValues.tattooLocation =
        existingAppointment.tattooLocation || '';
      tempNewInitValues.tattooSize = existingAppointment.tattooSize || '';
      tempNewInitValues.bodySide = existingAppointment.bodySide || '';

      tempNewInitValues.serviceId = existingAppointment.serviceId
        ? String(existingAppointment.serviceId)
        : '0';

      tempNewInitValues.piercingLocationId =
        existingAppointment.piercingLocationId
          ? String(existingAppointment.piercingLocationId)
          : '';

      if (existingAppointment.piercingLocationId) {
        tempNewInitValues.locationCategory =
          piercingLocations?.find(
            location =>
              location.locationId === existingAppointment.piercingLocationId,
          )?.locationCategory || '';
      }

      if (tempNewInitValues.priceType === 'FIXED') {
        tempNewInitValues.price = existingAppointment.price ?? 0;
      } else if (tempNewInitValues.priceType === 'RANGE') {
        tempNewInitValues.minPrice = existingAppointment.minPrice;
        tempNewInitValues.maxPrice = existingAppointment.maxPrice;
      } else {
        tempNewInitValues.hourlyPrice = existingAppointment.hourlyPrice;
      }

      if (
        loadedUser?.business.defaultTattooDeposit &&
        !selectedAppointment &&
        !hasFlashDesign
      ) {
        if (!isWalkInOrConsultation) {
          tempNewInitValues.depositPrice =
            loadedUser.business.defaultTattooDeposit;
        }
        if (!isConsultation) {
          tempNewInitValues.price = loadedUser.business.defaultTattooDeposit;
          if (tempNewInitValues.employeeId) {
            const etb = loadedUser?.business.employees.find(
              e => e.employeeId === tempNewInitValues.employeeId,
            );
            if (etb?.defaultHourlyRate) {
              tempNewInitValues.priceType = 'HOURLY';
              tempNewInitValues.hourlyPrice = etb.defaultHourlyRate;
              tempNewInitValues.price = undefined;
            }
          }
        }
      }

      tempNewInitValues.internalNotes =
        existingAppointment.internalNotes ?? undefined;

      const clientSource =
        existingAppointment.customerGuest ?? existingAppointment.customer;
      tempNewInitValues.customerGuestId =
        existingAppointment.customerGuest?.id ??
        existingAppointment.customerGuestId ??
        undefined;
      tempNewInitValues.firstName = clientSource.firstName ?? '';
      tempNewInitValues.lastName = clientSource.lastName ?? undefined;
      tempNewInitValues.email = clientSource.email ?? undefined;
      tempNewInitValues.phoneNumber =
        existingAppointment.customer.phoneNumber.replace('+', '');
      tempNewInitValues.dob = clientSource.dob
        ? new Date(clientSource.dob).toISOString().split('T')[0]
        : undefined;
      // The appointment-backed record, whether editing (selectedAppointment)
      // or scheduling an accepted request (selectedRequest)
      const waiverSource =
        selectedAppointment ??
        ('appointmentId' in existingAppointment
          ? existingAppointment
          : undefined);

      // Only a visible select is a real choice — undefined keeps the
      // persisted skipWaiver untouched on the server
      if (!showWaiverSelect) {
        tempNewInitValues.requestWaiver = undefined;
      } else if (waiverSource) {
        // Waiver requests are only created at confirm, so an unconfirmed
        // appointment's intent lives in skipWaiver, not waiverRequest
        tempNewInitValues.requestWaiver =
          (!!waiverSource.waiverRequest &&
            waiverSource.waiverRequest.status !== 'CANCELED') ||
          (!waiverSource.waiverRequest && !waiverSource.skipWaiver);
      } else {
        // A visible select implies a non-consultation with a published waiver
        tempNewInitValues.requestWaiver = true;
      }

      if (existingAppointment.refImgsId) {
        setImages(getExistingImgList(existingAppointment));
      }

      if (isConsultation && !existingAppointment.onschedId) {
        tempNewInitValues.allowOverlapping = true;
      }

      if (isConsultation) {
        tempNewInitValues.isVirtual = !!existingAppointment.isVirtual;
        tempNewInitValues.virtualMeetingUrl =
          existingAppointment.virtualMeetingUrl || '';
        tempNewInitValues.virtualMeetingProvider =
          (existingAppointment.virtualMeetingProvider ||
            null) as VirtualMeetingProvider | null;
        tempNewInitValues.priceType = 'FIXED';
        tempNewInitValues.depositPrice = 0;
        tempNewInitValues.price = 0;
        tempNewInitValues.hourlyPrice = 0;
      }

      tempNewInitValues.depositPaid =
        isAppointmentDepositPaid(existingAppointment);

      setLoadedInitValues(tempNewInitValues);
    }
  }, [
    selectedAppointment,
    piercingLocations,
    selectedRequest,
    initialAppointmentType,
    waiverData,
    hasWaiversFeature,
    loadedUser?.business.defaultTattooDeposit,
    loadedUser?.business.employees,
    loadedUser?.business?.id,
    cashDiscountEnabled,
  ]);

  const handleUpdateWaiverRequest = async (
    values: CreateAptValues,
    appointment: SelectedAppointment,
  ) => {
    if (!isAppointmentWaiverUpdatable(appointment.startDateTime)) return;
    // Never cancel a signed liability waiver via type conversion / edit
    if (appointment.waiverRequest?.status === 'SIGNED') return;

    const businessId = appointment.businessId || loadedUser?.business.id || '';

    if (values.requestWaiver) {
      if (!appointment.waiverRequest) {
        await requestWaiver({
          appointment,
        });
      } else if (appointment.waiverRequest?.status === 'CANCELED') {
        await reactivateWaiverRequest({
          businessId,
          waiverRequestId: appointment.waiverRequest.id,
        });
      }
    } else if (appointment.waiverRequest) {
      await cancelWaiverRequest({
        businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
    }
  };

  const onSubmitSuccess = async (
    args: CreateUpdateArgs & {
      values: CreateAptValues;
    },
  ) => {
    const appointmentId =
      args.newAptId ||
      ('appointment' in args && 'appointmentId' in args.appointment
        ? args.appointment.appointmentId
        : undefined);

    if (
      appointmentId &&
      args.values.appointmentType === 'NEW_APPOINTMENT' &&
      args.values.depositPaid &&
      (args.values.depositPrice ?? 0) > 0 &&
      (!selectedAppointment || !isAppointmentDepositPaid(selectedAppointment))
    ) {
      await markDepositPaid(appointmentId, {
        depositPaymentMethod: args.values.depositPaymentMethod || 'CASH',
      }).catch(() => {
        // Do not throw error if deposit is not marked as paid
      });
    }

    await searchClient.clearCache();

    await onCreateUpdateSuccess?.(args);
  };

  const updateExistingAppointment = async ({
    values,
    newImages,
    imagesToDelete,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
    imagesToDelete: string[];
  }) => {
    if (!selectedAppointment) return;
    try {
      const payload: Parameters<typeof updateAppointment>[0] = {
        ...values,
        newImages,
        imagesToDelete,
      };

      const updateAppointmentResponse = await updateAppointment(
        payload,
        selectedAppointment,
      );

      if (updateAppointmentResponse) {
        try {
          const aptCustomerId =
            updateAppointmentResponse.data.result.customerId;
          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );
        } catch {
          // Do not block update success on Algolia sync
        }

        try {
          const isBusinessChanging =
            !!values.businessId &&
            values.businessId !== selectedAppointment.businessId;
          const waiverAppointment = isBusinessChanging
            ? await getAppointmentById(selectedAppointment.appointmentId)
            : selectedAppointment;
          await handleUpdateWaiverRequest(values, waiverAppointment);
        } catch {
          toast.error(
            'Appointment updated, but the waiver could not be updated. Please check the waiver and try again.',
          );
        }

        const isQueueArtistAssigned =
          selectedAppointment.status === 'QUEUED' &&
          updateAppointmentResponse.data.result.status === 'SCHEDULED';

        toast.success(
          isQueueArtistAssigned
            ? 'Artist assigned to appointment!'
            : 'Appointment Updated!',
        );
        invalidateDepositTransferAppointments();

        if (calendarSelectedAppointment) {
          setSelectedAppointmentUpdated(true);
        }

        if (selectedAppointment.status === 'QUEUED') {
          if (isQueueArtistAssigned) {
            invalidateAppointments();
          }
          invalidateQueue();
          invalidateAppointment(selectedAppointment.appointmentId);
        }

        onClose();
        await onSubmitSuccess({
          type: 'update',
          appointment: selectedAppointment,
          values,
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error updating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    }
  };

  const scheduleApptFromRequest = async ({
    values,
    newImages,
    imagesToDelete,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
    imagesToDelete: string[];
  }) => {
    if (!selectedRequest) return;
    if (!values.firstName || !values.phoneNumber) return;
    try {
      const updateAppointmentResponse = await scheduleRequest(
        {
          ...values,
          imagesToDelete,
          newImages,
          businessId: loadedUser?.business.id || '',
        },
        selectedRequest,
      );

      if (updateAppointmentResponse) {
        try {
          const aptCustomerId =
            updateAppointmentResponse.data.result.customerId;
          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );
        } catch (error) {
          // Do something with the error
        }

        toast.success('Appointment Updated!');
        if (calendarSelectedAppointment) {
          setSelectedAppointmentUpdated(true);
        }

        const newAptId = updateAppointmentResponse.data.result.appointmentId;

        onClose();
        await onSubmitSuccess({
          type: 'update',
          appointment: selectedRequest,
          newAptId, // Send new Apt. ID in case we're scheduling a general request
          values,
        });

        if (values.requestWaiver) {
          resetWaiversPage();
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error updating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    }
  };

  const createNewAppointment = async ({
    values,
    newImages,
  }: {
    values: CreateAptValues;
    newImages: ImageItem[];
  }) => {
    if (!loadedUser) return;
    if (!values.firstName || !values.phoneNumber || !values.appointmentType)
      throw new Error('Please enter all client details and try again');

    try {
      const phoneNumber = !values.phoneNumber.startsWith('+')
        ? `+${values.phoneNumber}`
        : values.phoneNumber;
      const userRole = await getRoleByPhoneNumber(phoneNumber);
      const requireConfirmation = userRole && isUserEmployeeOrAdmin(userRole);
      if (requireConfirmation) {
        const accepted = await confirm({
          message:
            "You're scheduling with an artist. The client information you entered will not overwrite the existing client information. Are you sure you want to continue?",
          title: 'Scheduling with artist',
        });
        if (!accepted) return;
      }

      const appointmentResponse = await createAppointment(loadedUser, {
        ...values,
        newImages,
        businessId: loadedUser?.business.id || '',
      });

      if (appointmentResponse) {
        try {
          const aptCustomerId = appointmentResponse.data?.result?.customerId;

          await addCustomerAlgolia(
            aptCustomerId,
            loadedUser?.business.id || '',
          );
        } catch (error) {
          // Do something with the error
        }

        toast.success(
          values.isBookingLink ? 'Booking Link Sent!' : 'Appointment Created!',
        );
        onClose();
        await onSubmitSuccess({
          type: 'create',
          newAptId: appointmentResponse.data.result.appointmentId,
          values,
        });
        if (values.requestWaiver) {
          resetWaiversPage();
        }
        if (appointmentResponse.data.result.status === 'QUEUED') {
          invalidateQueue();
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        const message = getAppointmentFormErrorMessage(error);
        setFormError(message);
      } else {
        setFormError('An unexpected error occurred.'); // Some generic message
      }
      toast.error('Error creating appointment');
      if (setShouldScrollToTop) {
        setShouldScrollToTop(true);
      }
    } finally {
      closeLoadingModal();
    }
  };

  const isQueueWalkInForSchema = isQueueWalkIn({
    isWalkIn: loadedInitValues.appointmentType === 'WALK_IN',
    user: loadedUser as LoadedUser,
  });

  const validationSchema = getValidationSchema({
    isWalkIn: loadedInitValues.appointmentType === 'WALK_IN',
    user: loadedUser as LoadedUser,
    requireAppointmentLength: !isQueueWalkInForSchema,
    isEditingExisting: !!selectedAppointment,
  });

  return (
    <Box
      style={{
        width: '100%',
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (!canAddAppointment && !canManageAppointment) return;

          const isQueueWalkInSubmission = isQueueWalkIn({
            isWalkIn: values.appointmentType === 'WALK_IN',
            user: loadedUser as LoadedUser,
          });
          const isUnassignedAllowed = isUnassignedAppointmentAllowed({
            isWalkIn: values.appointmentType === 'WALK_IN',
            user: loadedUser as LoadedUser,
            isEditingExisting: !!selectedAppointment,
          });

          setFormError('');

          const newImages = images.filter(i => !i.isUploaded);
          const existingImages =
            selectedAppointment?.refImgsId ?? selectedRequest?.refImgsId ?? [];
          const imagesToDelete = existingImages.filter(
            img => !images.some(i => i.url === img),
          );

          const employee = loadedUser?.business.employees.find(
            e => e.employeeId === values.employeeId,
          );
          if (!employee && !isQueueWalkInSubmission && !isUnassignedAllowed) {
            setFormError('Employee not found');
            return;
          }

          // if time goes into next day, e.g. 11pm to 1am
          const isWalkIn = values.appointmentType === 'WALK_IN';
          const startDateTime = isWalkIn
            ? employee
              ? getWalkInStartDateTime()
              : null
            : values.startDateTime;
          const endDate = startDateTime
            ? addMinutes(
                new Date(startDateTime),
                parseInt(values.appointmentLengthInMinutes || '0', 10),
              )
            : null;

          const dateError =
            !!employee &&
            !!startDateTime &&
            !!endDate &&
            !values.isBookingLink &&
            getCrossDatelineError({
              startDateTime,
              endDateTime: endDate,
              additionalAppointments: values.additionalAppointments,
              appointment: selectedAppointment,
              timezone: employee.user?.timezone || DEFAULT_TIMEZONE,
            });

          if (dateError) {
            const accepted = await confirm(dateError);
            if (!accepted) return;
          }

          const priceError = validatePrice(
            values,
            values.appointmentType as AppointmentType,
          );
          if (priceError) {
            setFormError(priceError.error);
            return;
          }

          const isConsultation = values.appointmentType === 'CONSULTATION';

          updateLoadingModal({
            header: selectedAppointment
              ? 'Updating Appointment...'
              : values.isBookingLink
              ? 'Sending booking link...'
              : isConsultation
              ? 'Scheduling Consultation...'
              : 'Creating Appointment...',
            message: 'This could take a minute, please do not refresh the page',
            status: 'loading',
          });

          if (selectedAppointment) {
            await updateExistingAppointment({
              values,
              newImages,
              imagesToDelete,
            });
          } else if (selectedRequest) {
            await scheduleApptFromRequest({
              values,
              newImages,
              imagesToDelete,
            });
          } else if (
            values.firstName &&
            values.phoneNumber &&
            values.appointmentType
          ) {
            await createNewAppointment({
              values,
              newImages,
            });
          } else {
            setFormError('Please enter all client details and try again');
          }

          invalidateAvailability();

          setSubmitting(false);
          closeLoadingModal();
        }}>
        {({ isSubmitting, setFieldValue, submitForm, values, isValid }) => {
          const isQueueWalkInSubmission = isQueueWalkIn({
            isWalkIn: values.appointmentType === 'WALK_IN',
            user: loadedUser as LoadedUser,
          });
          const isUnassignedAllowed = isUnassignedAppointmentAllowed({
            isWalkIn: values.appointmentType === 'WALK_IN',
            user: loadedUser as LoadedUser,
            isEditingExisting: !!selectedAppointment,
          });

          const isNewWalkIn =
            !selectedAppointment && values.appointmentType === 'WALK_IN';

          const submitDisabled = isNewWalkIn
            ? isSubmitting ||
              !isValid ||
              !values.phoneNumber ||
              !values.firstName ||
              !values.serviceId ||
              (!values.appointmentLengthInMinutes && !isQueueWalkInSubmission)
            : isSubmitting ||
              !isValid ||
              (!values.appointmentLengthInMinutes &&
                !isQueueWalkInSubmission) ||
              (!values.employeeId &&
                !isQueueWalkInSubmission &&
                !isUnassignedAllowed) ||
              (!values.startDateTime &&
                !values.isBookingLink &&
                selectedAppointment?.status !== 'QUEUED') ||
              !values.firstName ||
              !values.phoneNumber ||
              !values.serviceId ||
              (!canAddAppointment && !canManageAppointment);

          const submitText = isNewWalkIn
            ? 'Add Walk In'
            : selectedAppointment
            ? 'Save Changes'
            : values.isBookingLink
            ? 'Send Booking Link'
            : values.appointmentType === 'CONSULTATION'
            ? 'Schedule Consultation'
            : values.isTouchUp
            ? 'Schedule Touch-Up'
            : 'Schedule Appointment';

          const sections = (
            <>
              <ClientSearchAutofill
                selectedAppointment={selectedAppointment}
                selectedRequest={selectedRequest}
                allowCustomerWithoutAppointment={
                  !!values.isFromExistingAppointment
                }
                showScheduleBlockWarning
              />
              <Form>
                {formError && (
                  <Typography variant="body1" color="primary">
                    {formError}
                  </Typography>
                )}
                <AppointmentDetails
                  selectedAppointment={selectedAppointment}
                  disableDepositPaid={
                    !!selectedRequest &&
                    'appointmentId' in selectedRequest &&
                    isAppointmentDepositPaid(selectedRequest)
                  }
                  hasPersistedWaiverChoice={
                    !!selectedRequest && 'appointmentId' in selectedRequest
                  }
                />

                <AppointmentDate
                  allowSwitchToBookingLink={allowSwitchToBookingLink}
                />

                <TattooDetails images={images} setImages={setImages} />
                <InternalNotes />
                <Button
                  type="submit"
                  disabled={submitDisabled}
                  variant="contained"
                  className="primary"
                  sx={redesignActive ? headerSubmitSx : undefined}>
                  {submitText}
                </Button>
              </Form>
            </>
          );

          return (
            <>
              {!selectedAppointment &&
                !selectedRequest &&
                !values.appointmentType && (
                  <AppointmentTypeComponent
                    value={values.appointmentType as DBAppointmentType}
                    setFieldValue={value =>
                      setFieldValue('appointmentType', value)
                    }
                  />
                )}

              {(selectedAppointment || values.appointmentType) && (
                <Box
                  className={cx('composer', redesignActive && 'composer-wide')}>
                  {redesignActive && (
                    <ModalHeaderBadge label={getTypeLabel(values)} />
                  )}
                  <Box className="composer-body">
                    <Box className="composer-sections">{sections}</Box>
                    {redesignActive && (
                      <Box className="composer-summary">
                        <AppointmentSummary
                          submitText={submitText}
                          disabled={submitDisabled}
                          isSubmitting={isSubmitting}
                          onSubmit={submitForm}
                          condensed={isSummaryDocked}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </>
          );
        }}
      </Formik>
    </Box>
  );
}
